import type { Letter } from "@repo/hindi-shared";
import { letterById, mulberry32, pickDistractors } from "@repo/hindi-shared";
import { useEffect, useMemo, useRef, useState } from "react";

import { DualLabel } from "../ui/DualLabel";
import { GlyphTile } from "../ui/GlyphTile";
import { InkButton } from "../ui/InkButton";
import { SpeakerIcon } from "../ui/SpeakerIcon";

import { gradeFromChoice } from "./grading";
import { dateKey, hashSeed } from "./seed";
import type { ExerciseProps } from "./types";

const CORRECT_FLASH_MS = 450;

/** Fisher-Yates shuffle using an injected `[0, 1)` rand source. Non-mutating. */
function shuffle<T>(items: readonly T[], rand: () => number): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = copy[i]!;
    copy[i] = copy[j]!;
    copy[j] = tmp;
  }
  return copy;
}

/**
 * SoundToSymbol — plays the target's sound, then asks the learner to pick
 * its glyph out of 6 tiles (the target plus 5 seeded distractors). Wrong
 * picks mark that tile "wrong" and keep accepting; the right pick flashes
 * "correct" before completing.
 */
export function SoundToSymbol({ exercise, audio, onComplete }: ExerciseProps) {
  const letter = letterById.get(exercise.itemId);

  // Seeded by itemId + today's date, so the tile set (and target position)
  // is stable across re-renders within a session but varies day to day.
  const tileIds = useMemo(() => {
    if (letter === undefined) return [];
    const rand = mulberry32(hashSeed(exercise.itemId + dateKey()));
    const distractors = pickDistractors(exercise.itemId, 5, rand);
    return shuffle([exercise.itemId, ...distractors], rand);
  }, [exercise.itemId, letter]);

  const [attempts, setAttempts] = useState(0);
  const [wrongIds, setWrongIds] = useState<Set<string>>(new Set());
  const [correctId, setCorrectId] = useState<string | null>(null);

  const cancelledRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (letter !== undefined) {
      void audio.speak(letter.audioText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- speak once per mount
  }, []);

  if (letter === undefined) {
    return null;
  }

  const audioText = letter.audioText;

  function handleReplay(): void {
    void audio.speak(audioText);
  }

  function handlePick(pickedId: string): void {
    if (correctId !== null) return; // already answered

    const nextAttempts = attempts + 1;
    setAttempts(nextAttempts);

    if (pickedId === exercise.itemId) {
      setCorrectId(pickedId);
      const grade = gradeFromChoice(nextAttempts);
      window.setTimeout(() => {
        if (cancelledRef.current) return;
        onComplete({ grade, itemId: exercise.itemId });
      }, CORRECT_FLASH_MS);
    } else {
      setWrongIds((prev) => new Set(prev).add(pickedId));
    }
  }

  function tileState(id: string): "idle" | "correct" | "wrong" {
    if (correctId === id) return "correct";
    if (wrongIds.has(id)) return "wrong";
    return "idle";
  }

  return (
    <div className="flex flex-col items-center gap-8">
      <DualLabel
        en="Tap the letter you heard"
        hi="जो सुना वही अक्षर छुओ"
        className="font-latin text-sm text-ink-soft"
      />

      <InkButton variant="ghost" onClick={handleReplay}>
        <SpeakerIcon className="mr-2" />
        <DualLabel variant="inline" en="Replay" hi="फिर सुनो" />
      </InkButton>

      <div className="grid grid-cols-3 gap-4">
        {tileIds.map((id) => {
          const tileLetter: Letter | undefined = letterById.get(id);
          if (tileLetter === undefined) return null;
          return (
            <GlyphTile
              key={id}
              glyph={tileLetter.glyph}
              state={tileState(id)}
              size="lg"
              disabled={correctId !== null || wrongIds.has(id)}
              onClick={() => handlePick(id)}
            />
          );
        })}
      </div>
    </div>
  );
}
